import { useEffect, useState } from 'react'
import { useNavigate, useSearch, Link } from '@tanstack/react-router'
import { Loader2, Save, Check, X, ExternalLink, CreditCard, AlertTriangle, Link2Off, FileText, ChevronRight, Plus, Trash2, Bell } from 'lucide-react'
import { useAuthStore } from '../lib/auth'
import { api, type Organisation, type Currency, type StripeConnectStatus, type PaymentReminderSettings, type PaymentReminderRule } from '../lib/api'
import { DashboardLayout } from '../components/DashboardLayout'
import { PageTabs } from '../components/PageTabs'

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)
  
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  
  return debouncedValue
}

export function SettingsPage() {
  const navigate = useNavigate()
  const searchParams = useSearch({ from: '/dashboard/settings' }) as { stripe?: string }
  const { user, currentOrg, memberships, isAuthenticated, isLoading: authLoading, checkAuth } = useAuthStore()
  
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  // Form state
  const [orgName, setOrgName] = useState('')
  const [orgSlug, setOrgSlug] = useState('')
  const [originalSlug, setOriginalSlug] = useState('')
  const [slugError, setSlugError] = useState('')
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null)
  const [isCheckingSlug, setIsCheckingSlug] = useState(false)
  const [currency, setCurrency] = useState('USD')
  const [bookingLeadTime, setBookingLeadTime] = useState(0)
  const [maxBookingDuration, setMaxBookingDuration] = useState(8)
  const [requireApproval, setRequireApproval] = useState(false)
  const [allowPublicBookings, setAllowPublicBookings] = useState(false)
  const [galleryIntro, setGalleryIntro] = useState('')
  const [donationUrl, setDonationUrl] = useState('')
  const [donationMode, setDonationMode] = useState<'link' | 'stripe'>('link')
  
  // Payment reminder state
  const [remindersEnabled, setRemindersEnabled] = useState(false)
  const [autoCancelDaysBefore, setAutoCancelDaysBefore] = useState(5)
  const [immediatePaymentHours, setImmediatePaymentHours] = useState(24)
  const [reminderRules, setReminderRules] = useState<PaymentReminderRule[]>([])
  
  // Debounce slug for availability check
  const debouncedSlug = useDebounce(orgSlug, 500)
  
  // Available currencies
  const [currencies, setCurrencies] = useState<Currency[]>([])
  
  // Stripe state
  const [stripeStatus, setStripeStatus] = useState<StripeConnectStatus | null>(null)
  const [stripeLoading, setStripeLoading] = useState(false)
  const [stripeError, setStripeError] = useState<string | null>(null)
  const [disconnecting, setDisconnecting] = useState(false)
  
  useEffect(() => {
    checkAuth()
  }, [])
  
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate({ to: '/login' })
    }
  }, [authLoading, isAuthenticated, navigate])
  
  // Redirect non-admins
  useEffect(() => {
    if (!authLoading && isAuthenticated && currentOrg?.role !== 'admin') {
      navigate({ to: '/dashboard' })
    }
  }, [authLoading, isAuthenticated, currentOrg, navigate])
  
  // Wait for auth to complete before loading data
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadSettings()
      loadStripeStatus()
    }
  }, [authLoading, isAuthenticated])
  
  // Handle Stripe return from OAuth
  useEffect(() => {
    if (searchParams?.stripe === 'success') {
      setSuccess('Stripe account connected successfully!')
      loadStripeStatus()
      // Clear the URL param
      navigate({ to: '/dashboard/settings', replace: true })
    } else if (searchParams?.stripe === 'refresh') {
      // Onboarding link expired, need to generate a new one
      loadStripeStatus()
      navigate({ to: '/dashboard/settings', replace: true })
    }
  }, [searchParams])
  
  // Check slug availability when debounced slug changes
  useEffect(() => {
    const checkSlug = async () => {
      if (!debouncedSlug || debouncedSlug === originalSlug) {
        setSlugAvailable(null)
        setSlugError('')
        return
      }
      
      setIsCheckingSlug(true)
      try {
        const result = await api.settings.checkSlugAvailability(debouncedSlug)
        setSlugAvailable(result.available)
        setSlugError(result.error || '')
      } catch (err: any) {
        setSlugError(err.message || 'Failed to check availability')
        setSlugAvailable(false)
      } finally {
        setIsCheckingSlug(false)
      }
    }
    
    checkSlug()
  }, [debouncedSlug, originalSlug])
  
  const loadSettings = async () => {
    try {
      setIsLoading(true)
      const [settingsData, currenciesData] = await Promise.all([
        api.settings.get(),
        api.settings.getCurrencies(),
      ])
      
      const org = settingsData.organisation
      setOrgName(org.name)
      setOrgSlug(org.slug)
      setOriginalSlug(org.slug)
      setCurrency(org.settings?.currency || 'USD')
      setBookingLeadTime(org.settings?.bookingLeadTime || 0)
      setMaxBookingDuration(org.settings?.maxBookingDuration || 8)
      setRequireApproval(org.settings?.requireApprovalByDefault || false)
      setAllowPublicBookings(org.settings?.allowPublicBookings || false)
      setGalleryIntro(org.settings?.galleryIntro || '')
      setDonationUrl(org.settings?.donationUrl || '')
      setDonationMode(org.settings?.donationMode === 'stripe' ? 'stripe' : 'link')
      
      // Payment reminders
      const pr = org.settings?.paymentReminders
      if (pr) {
        setRemindersEnabled(pr.enabled ?? false)
        setAutoCancelDaysBefore(pr.autoCancelDaysBefore ?? 5)
        setImmediatePaymentHours(pr.immediatePaymentHours ?? 24)
        setReminderRules(pr.reminders ?? [])
      }
      
      setCurrencies(currenciesData.currencies)
    } catch (err: any) {
      setError(err.message || 'Failed to load settings')
    } finally {
      setIsLoading(false)
    }
  }
  
  const loadStripeStatus = async () => {
    try {
      setStripeLoading(true)
      const status = await api.stripe.getConnectStatus()
      setStripeStatus(status)
    } catch (err: any) {
      setStripeError(err.message || 'Failed to load Stripe status')
    } finally {
      setStripeLoading(false)
    }
  }
  
  const handleConnectStripe = async () => {
    try {
      setStripeLoading(true)
      setStripeError(null)
      const { url } = await api.stripe.getConnectUrl()
      window.location.href = url
    } catch (err: any) {
      setStripeError(err.message || 'Failed to start Stripe connection')
      setStripeLoading(false)
    }
  }
  
  const handleContinueOnboarding = async () => {
    try {
      setStripeLoading(true)
      setStripeError(null)
      const { url } = await api.stripe.getOnboardingUrl()
      window.location.href = url
    } catch (err: any) {
      setStripeError(err.message || 'Failed to get onboarding link')
      setStripeLoading(false)
    }
  }
  
  const handleDisconnectStripe = async () => {
    if (!confirm('Are you sure you want to disconnect your Stripe account? This will disable payment collection for bookings.')) {
      return
    }
    try {
      setDisconnecting(true)
      await api.stripe.disconnect()
      setStripeStatus({ connected: false, stripeConfigured: true })
      setSuccess('Stripe account disconnected')
    } catch (err: any) {
      setStripeError(err.message || 'Failed to disconnect Stripe')
    } finally {
      setDisconnecting(false)
    }
  }
  
  const handleOpenStripeDashboard = async () => {
    try {
      const { url } = await api.stripe.getDashboardUrl()
      window.open(url, '_blank')
    } catch (err: any) {
      setStripeError(err.message || 'Failed to open Stripe dashboard')
    }
  }
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    
    if (!orgName.trim()) {
      setError('Organisation name is required')
      return
    }
    
    // Validate slug if changed
    if (orgSlug !== originalSlug) {
      if (slugError) {
        setError(slugError)
        return
      }
      if (slugAvailable === false) {
        setError('Please choose an available slug')
        return
      }
    }
    
    setIsSaving(true)
    
    try {
      // Update slug if changed
      if (orgSlug !== originalSlug && orgSlug.trim()) {
        const newSlug = orgSlug.trim().toLowerCase()
        const result = await api.settings.updateSlug(newSlug)
        // Update both states with the confirmed slug from the server
        const confirmedSlug = result.organisation.slug
        setOrgSlug(confirmedSlug)
        setOriginalSlug(confirmedSlug)
      }
      
      await api.settings.update({
        name: orgName.trim(),
        settings: {
          currency,
          bookingLeadTime,
          maxBookingDuration,
          requireApprovalByDefault: requireApproval,
          allowPublicBookings,
          galleryIntro: galleryIntro.trim() || undefined,
          donationUrl: donationUrl.trim(),
          // Card donations need a connected Stripe account; fall back to the link mode
          donationMode:
            donationMode === 'stripe' && !stripeStatus?.chargesEnabled ? 'link' : donationMode,
          paymentReminders: {
            enabled: remindersEnabled,
            autoCancelDaysBefore,
            immediatePaymentHours,
            reminders: reminderRules,
          },
        },
      })
      setSuccess('Settings saved successfully')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err: any) {
      setError(err.message || 'Failed to save settings')
    } finally {
      setIsSaving(false)
    }
  }
  
  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }
  
  if (!isAuthenticated || !user) {
    return null
  }

  const currentMembership = memberships.find(m => m.orgSlug === currentOrg?.orgSlug)
  const isAdmin = currentMembership?.role === 'admin'

  return (
    <DashboardLayout title="Settings">
      {isAdmin && (
        <PageTabs
          tabs={[
            { label: 'Settings', to: '/dashboard/settings' },
            { label: 'Audit', to: '/dashboard/audit' },
          ]}
          active="/dashboard/settings"
        />
      )}
      <div className="max-w-3xl">
        {!isAdmin && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg text-sm">
            You need admin permissions to modify settings.
          </div>
        )}
        
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
            {success}
          </div>
        )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Organisation Details */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">Organisation Details</h2>
              
              <div>
                <label htmlFor="orgName" className="block text-sm font-medium text-gray-700 mb-1">
                  Organisation Name *
                </label>
                <input
                  id="orgName"
                  type="text"
                  required
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  disabled={!isAdmin}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="My Organisation"
                />
              </div>
              
              <div>
                <label htmlFor="orgSlug" className="block text-sm font-medium text-gray-700 mb-1">
                  Public URL Slug
                </label>
                <div className="relative">
                  <div className="flex items-center">
                    <span className="px-3 py-2 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-sm text-gray-500">
                      /marketplace/
                    </span>
                    <input
                      id="orgSlug"
                      type="text"
                      value={orgSlug}
                      onChange={(e) => setOrgSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      disabled={!isAdmin}
                      className={`flex-1 px-3 py-2 border rounded-r-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500 ${
                        slugError && orgSlug !== originalSlug
                          ? 'border-red-300'
                          : slugAvailable && orgSlug !== originalSlug
                          ? 'border-green-300'
                          : 'border-gray-300'
                      }`}
                      placeholder="my-organisation"
                    />
                    {isCheckingSlug && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                      </div>
                    )}
                    {!isCheckingSlug && slugAvailable && orgSlug !== originalSlug && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Check className="w-4 h-4 text-green-500" />
                      </div>
                    )}
                    {!isCheckingSlug && slugError && orgSlug !== originalSlug && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <X className="w-4 h-4 text-red-500" />
                      </div>
                    )}
                  </div>
                </div>
                {slugError && orgSlug !== originalSlug ? (
                  <p className="mt-1 text-xs text-red-500">{slugError}</p>
                ) : slugAvailable && orgSlug !== originalSlug ? (
                  <p className="mt-1 text-xs text-green-600">This slug is available!</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">
                    This is the URL where your public room gallery will be accessible.
                    {orgSlug && (
                      <a 
                        href={`/marketplace/${orgSlug}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="ml-1 text-primary-600 hover:text-primary-700 inline-flex items-center gap-1"
                      >
                        View gallery <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </p>
                )}
              </div>
              
              <div>
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
                  Currency
                </label>
                <select
                  id="currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  disabled={!isAdmin}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
                >
                  {currencies.map(curr => (
                    <option key={curr.code} value={curr.code}>
                      {curr.symbol} - {curr.name} ({curr.code})
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Used for displaying room rates and booking prices
                </p>
              </div>
              
              <div>
                <label htmlFor="galleryIntro" className="block text-sm font-medium text-gray-700 mb-1">
                  Gallery Introduction
                </label>
                <textarea
                  id="galleryIntro"
                  value={galleryIntro}
                  onChange={(e) => setGalleryIntro(e.target.value)}
                  disabled={!isAdmin}
                  rows={4}
                  maxLength={2000}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500 resize-none"
                  placeholder="Welcome to our rooms! We offer a variety of spaces for meetings, workshops, and events..."
                />
                <p className="mt-1 text-xs text-gray-500">
                  This text will appear on your public gallery page above the list of available rooms
                </p>
              </div>
            </div>

            {/* Donations */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Donations</h2>
                <p className="mt-1 text-sm text-gray-500">
                  These settings are for guests you have marked as voluntary donation. Instead of
                  paying a room rate, they are invited to make a donation. Only the method you
                  choose here is used.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  How guests make donations
                </label>
                <div className="space-y-2">
                  <label
                    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      donationMode === 'link'
                        ? 'border-primary-300 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="donationMode"
                      value="link"
                      checked={donationMode === 'link'}
                      onChange={() => setDonationMode('link')}
                      disabled={!isAdmin}
                      className="w-4 h-4 mt-0.5 text-primary-600 border-gray-300 focus:ring-primary-500 disabled:opacity-50"
                    />
                    <span>
                      <span className="block text-sm font-medium text-gray-900">
                        Donation page link
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5">
                        Guests are emailed the donation page link below after their booking ends.
                      </span>
                    </span>
                  </label>

                  <label
                    className={`flex items-start gap-3 p-3 border rounded-lg transition-colors ${
                      !stripeStatus?.chargesEnabled
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                        : donationMode === 'stripe'
                          ? 'border-primary-300 bg-primary-50 cursor-pointer'
                          : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                    }`}
                  >
                    <input
                      type="radio"
                      name="donationMode"
                      value="stripe"
                      checked={donationMode === 'stripe'}
                      onChange={() => setDonationMode('stripe')}
                      disabled={!isAdmin || !stripeStatus?.chargesEnabled}
                      className="w-4 h-4 mt-0.5 text-primary-600 border-gray-300 focus:ring-primary-500 disabled:opacity-50"
                    />
                    <span>
                      <span
                        className={`block text-sm font-medium ${
                          stripeStatus?.chargesEnabled ? 'text-gray-900' : 'text-gray-400'
                        }`}
                      >
                        Card payment through Roomsi
                      </span>
                      <span
                        className={`block text-xs mt-0.5 ${
                          stripeStatus?.chargesEnabled ? 'text-gray-500' : 'text-gray-400'
                        }`}
                      >
                        Guests choose an amount when they book and pay by card. They see the card
                        fees and what you will receive before they pay. The donation page link
                        below is not used in this mode.
                        {!stripeStatus?.chargesEnabled &&
                          ' Available once your Stripe account is connected below.'}
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label htmlFor="donationUrl" className="block text-sm font-medium text-gray-700 mb-1">
                  Donation Page Link
                </label>
                <input
                  id="donationUrl"
                  type="url"
                  value={donationUrl}
                  onChange={(e) => setDonationUrl(e.target.value)}
                  disabled={!isAdmin}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="https://www.crowdfunder.co.uk/p/your-organisation"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {donationMode === 'stripe' && stripeStatus?.chargesEnabled
                    ? 'Not used while card payment is selected. It is kept as a backup so guests can still be invited to donate if your Stripe account is ever disconnected.'
                    : 'Guests with the voluntary donation option are emailed this link after their booking ends, inviting them to support your organisation.'}
                </p>
              </div>
            </div>
            
            {/* Payments / Stripe Connect (Admin Only) */}
            {isAdmin && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Payments</h2>
                    <p className="text-sm text-gray-500">Connect your Stripe account to accept payments for room bookings</p>
                  </div>
                  <CreditCard className="w-8 h-8 text-gray-400" />
                </div>
                
                {stripeError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span>{stripeError}</span>
                  </div>
                )}
                
                {stripeLoading && !stripeStatus ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : !stripeStatus?.stripeConfigured ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
                    <p className="font-medium">Stripe is not configured</p>
                    <p className="text-sm mt-1">Contact your platform administrator to enable payment processing.</p>
                  </div>
                ) : !stripeStatus?.connected ? (
                  <div className="space-y-4">
                    <p className="text-gray-600">
                      Connect your Stripe account to start accepting payments. You'll be redirected to Stripe to complete the setup.
                    </p>
                    <button
                      onClick={handleConnectStripe}
                      disabled={stripeLoading}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#635BFF] text-white rounded-lg hover:bg-[#5851DB] transition-colors disabled:opacity-50"
                    >
                      {stripeLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4" />
                      )}
                      Connect with Stripe
                    </button>
                  </div>
                ) : !stripeStatus?.detailsSubmitted ? (
                  <div className="space-y-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-yellow-800 font-medium">
                        <AlertTriangle className="w-4 h-4" />
                        Onboarding Incomplete
                      </div>
                      <p className="text-sm text-yellow-700 mt-1">
                        You've started connecting your Stripe account, but there are still details to complete before you can accept payments.
                      </p>
                    </div>
                    <button
                      onClick={handleContinueOnboarding}
                      disabled={stripeLoading}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#635BFF] text-white rounded-lg hover:bg-[#5851DB] transition-colors disabled:opacity-50"
                    >
                      {stripeLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <ExternalLink className="w-4 h-4" />
                      )}
                      Continue Setup
                    </button>
                  </div>
                ) : !stripeStatus?.chargesEnabled ? (
                  <div className="space-y-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-yellow-800 font-medium">
                        <AlertTriangle className="w-4 h-4" />
                        Payments Not Yet Enabled
                      </div>
                      <p className="text-sm text-yellow-700 mt-1">
                        Your Stripe account is connected but Stripe is still reviewing your details. This usually takes 1-2 business days.
                      </p>
                    </div>
                    <button
                      onClick={handleOpenStripeDashboard}
                      className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View Stripe Dashboard
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-green-800 font-medium">
                        <Check className="w-4 h-4" />
                        Stripe Account Connected
                      </div>
                      <p className="text-sm text-green-700 mt-1">
                        Your account is fully set up and ready to accept payments.
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleOpenStripeDashboard}
                        className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Stripe Dashboard
                      </button>
                      <button
                        onClick={handleDisconnectStripe}
                        disabled={disconnecting}
                        className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                      >
                        {disconnecting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Link2Off className="w-4 h-4" />
                        )}
                        Disconnect
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Booking Settings */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">Booking Settings</h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="leadTime" className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Lead Time (hours)
                  </label>
                  <input
                    id="leadTime"
                    type="number"
                    min={0}
                    value={bookingLeadTime}
                    onChange={(e) => setBookingLeadTime(parseInt(e.target.value) || 0)}
                    disabled={!isAdmin}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    How far in advance bookings must be made
                  </p>
                </div>
                
                <div>
                  <label htmlFor="maxDuration" className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Duration (hours)
                  </label>
                  <input
                    id="maxDuration"
                    type="number"
                    min={1}
                    value={maxBookingDuration}
                    onChange={(e) => setMaxBookingDuration(parseInt(e.target.value) || 1)}
                    disabled={!isAdmin}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Maximum length of a single booking
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <input
                  id="requireApproval"
                  type="checkbox"
                  checked={requireApproval}
                  onChange={(e) => setRequireApproval(e.target.checked)}
                  disabled={!isAdmin}
                  className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 disabled:opacity-50"
                />
                <label htmlFor="requireApproval" className="text-sm font-medium text-gray-700">
                  Require approval for new bookings by default
                </label>
              </div>
              
              <div className="flex items-start gap-3">
                <input
                  id="allowPublicBookings"
                  type="checkbox"
                  checked={allowPublicBookings}
                  onChange={(e) => setAllowPublicBookings(e.target.checked)}
                  disabled={!isAdmin}
                  className="w-4 h-4 mt-0.5 text-primary-600 rounded border-gray-300 focus:ring-primary-500 disabled:opacity-50"
                />
                <div>
                  <label htmlFor="allowPublicBookings" className="text-sm font-medium text-gray-700">
                    Allow public bookings
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    When enabled, anyone can request a booking from your public gallery without needing an invite first. They only need to provide their email address.
                    This does not skip your approval rules: if a booking would normally need approval, public bookings still wait for your approval before they are confirmed.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Payment Reminders (Admin Only, requires Stripe) */}
            {isAdmin && stripeStatus?.chargesEnabled && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Payment Reminders</h2>
                    <p className="text-sm text-gray-500">Automatically chase guests who have unpaid bookings</p>
                  </div>
                  <Bell className="w-6 h-6 text-gray-400" />
                </div>
                
                <div className="flex items-start gap-3">
                  <input
                    id="remindersEnabled"
                    type="checkbox"
                    checked={remindersEnabled}
                    onChange={(e) => {
                      setRemindersEnabled(e.target.checked)
                      // Pre-populate defaults when enabling for the first time
                      if (e.target.checked && reminderRules.length === 0) {
                        setReminderRules([
                          { id: crypto.randomUUID().slice(0, 8), reference: 'after_approval', days: 1, enabled: true },
                          { id: crypto.randomUUID().slice(0, 8), reference: 'after_approval', days: 3, enabled: true },
                          { id: crypto.randomUUID().slice(0, 8), reference: 'before_booking', days: 7, enabled: true },
                          { id: crypto.randomUUID().slice(0, 8), reference: 'before_booking', days: 3, enabled: false },
                        ])
                      }
                    }}
                    className="w-4 h-4 mt-0.5 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                  />
                  <div>
                    <label htmlFor="remindersEnabled" className="text-sm font-medium text-gray-700">
                      Send payment reminders for unpaid bookings
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      When enabled, guests with outstanding payments will receive email reminders at the times you configure below.
                    </p>
                  </div>
                </div>
                
                {remindersEnabled && (
                  <div className="space-y-6 pt-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="autoCancelDays" className="block text-sm font-medium text-gray-700 mb-1">
                          Auto-cancel (days before booking)
                        </label>
                        <input
                          id="autoCancelDays"
                          type="number"
                          min={1}
                          max={30}
                          value={autoCancelDaysBefore}
                          onChange={(e) => setAutoCancelDaysBefore(parseInt(e.target.value) || 5)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Unpaid bookings are cancelled this many days before the booking date
                        </p>
                      </div>
                      
                      <div>
                        <label htmlFor="immediateHours" className="block text-sm font-medium text-gray-700 mb-1">
                          Late booking payment (hours)
                        </label>
                        <input
                          id="immediateHours"
                          type="number"
                          min={1}
                          max={168}
                          value={immediatePaymentHours}
                          onChange={(e) => setImmediatePaymentHours(parseInt(e.target.value) || 24)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          For last-minute bookings, guests must pay within this many hours
                        </p>
                      </div>
                    </div>
                    
                    {/* Reminder rules list */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Reminder Schedule
                        </label>
                        {reminderRules.length < 10 && (
                          <button
                            type="button"
                            onClick={() => {
                              setReminderRules([
                                ...reminderRules,
                                { id: crypto.randomUUID().slice(0, 8), reference: 'after_approval', days: 1, enabled: true },
                              ])
                            }}
                            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                          >
                            <Plus className="w-4 h-4" />
                            Add Reminder
                          </button>
                        )}
                      </div>
                      
                      {reminderRules.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">No reminders configured. Add at least one reminder.</p>
                      ) : (
                        <div className="space-y-2">
                          {reminderRules.map((rule, index) => (
                            <div
                              key={rule.id}
                              className={`flex items-center gap-3 p-3 rounded-lg border ${
                                rule.enabled ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={rule.enabled}
                                onChange={(e) => {
                                  const updated = [...reminderRules]
                                  updated[index] = { ...rule, enabled: e.target.checked }
                                  setReminderRules(updated)
                                }}
                                className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                              />
                              
                              <input
                                type="number"
                                min={1}
                                max={90}
                                value={rule.days}
                                onChange={(e) => {
                                  const updated = [...reminderRules]
                                  updated[index] = { ...rule, days: parseInt(e.target.value) || 1 }
                                  setReminderRules(updated)
                                }}
                                className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              />
                              
                              <span className="text-sm text-gray-600">days</span>
                              
                              <select
                                value={rule.reference}
                                onChange={(e) => {
                                  const updated = [...reminderRules]
                                  updated[index] = { ...rule, reference: e.target.value as 'after_approval' | 'before_booking' }
                                  setReminderRules(updated)
                                }}
                                className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              >
                                <option value="after_approval">after booking is approved</option>
                                <option value="before_booking">before the booking date</option>
                              </select>
                              
                              <button
                                type="button"
                                onClick={() => {
                                  setReminderRules(reminderRules.filter((_, i) => i !== index))
                                }}
                                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                title="Remove reminder"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Human-readable summary */}
                      {reminderRules.filter(r => r.enabled).length > 0 && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <span className="font-medium">Summary:</span> Reminders will be sent{' '}
                            {reminderRules
                              .filter(r => r.enabled)
                              .sort((a, b) => {
                                if (a.reference === b.reference) return a.days - b.days
                                return a.reference === 'after_approval' ? -1 : 1
                              })
                              .map((r, i, arr) => {
                                const text = r.reference === 'after_approval'
                                  ? `${r.days} day${r.days !== 1 ? 's' : ''} after approval`
                                  : `${r.days} day${r.days !== 1 ? 's' : ''} before the booking`
                                if (i === arr.length - 1 && i > 0) return ` and ${text}`
                                if (i > 0) return `, ${text}`
                                return text
                              })
                              .join('')}
                            . Unpaid bookings will be cancelled {autoCancelDaysBefore} day{autoCancelDaysBefore !== 1 ? 's' : ''} before the booking date.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Terms & PLI Settings Link - Admin Only */}
            {isAdmin && (
              <Link
                to="/dashboard/settings/terms"
                className="block bg-white rounded-xl border border-gray-200 p-6 hover:border-primary-300 hover:bg-primary-50/30 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 group-hover:bg-primary-200 transition-colors">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Terms of Use & Activity Types</h3>
                      <p className="text-sm text-gray-500">
                        Configure terms guests must accept and manage activity types with PLI requirements
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600 transition-colors" />
                </div>
              </Link>
            )}
            
            {isAdmin && (
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Settings
                    </>
                  )}
                </button>
              </div>
            )}
          </form>
        </div>
    </DashboardLayout>
  )
}
